// Pull StealthSeminar's autologin_id off the registration confirmation page.
// autologin_id is the canonical vendor_event_id (it lands in both the webhook
// payload at data.autologin_id and is exposed browser-side on the SS page).

export interface SSExtraction {
  vendor_event_id: string;
}

export function extractStealthSeminar(): SSExtraction | null {
  const id =
    readFromGlobals()
    ?? readFromDom()
    ?? readFromUrl();

  return id ? { vendor_event_id: id } : null;
}

function readFromGlobals(): string | null {
  const g = window as unknown as Record<string, unknown>;

  // Most likely: a `lead` (or similar) object exposed by SS with autologin_id
  for (const key of ['lead', 'registration', '__SS_REGISTRATION__', 'SS_REGISTRATION', 'stealthseminar']) {
    const obj = g[key];
    if (obj && typeof obj === 'object') {
      const id = pickAutologin(obj as Record<string, unknown>);
      if (id) return id;
    }
  }

  // Top-level globals
  for (const key of ['autologin_id', 'autologin', 'SS_AUTOLOGIN_ID']) {
    const v = g[key];
    if (typeof v === 'string' && v) return v;
  }

  return null;
}

function pickAutologin(obj: Record<string, unknown>): string | null {
  const candidates = ['autologin_id', 'autologinId', 'autologin', 'registration_id', 'session_id', 'id'];
  for (const k of candidates) {
    const v = obj[k];
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return null;
}

function readFromDom(): string | null {
  const el = document.querySelector<HTMLElement>(
    '[data-autologin-id], [data-autologin], [data-registration-id], [data-ss-autologin]'
  );
  return el?.dataset.autologinId
      ?? el?.dataset.autologin
      ?? el?.dataset.registrationId
      ?? el?.dataset.ssAutologin
      ?? null;
}

function readFromUrl(): string | null {
  const url = new URL(window.location.href);
  // SS confirmation/auto-login URLs use a short `id` query param as the
  // autologin_id. Path varies across the flow (/t/, /a/, etc.) and the
  // SPA can rewrite it client-side, so we don't gate on path. Only the
  // SS extractor is invoked when source=stealthseminar, so this param
  // can't bleed into other platforms.
  return url.searchParams.get('autologin_id')
      ?? url.searchParams.get('autologin')
      ?? url.searchParams.get('registration_id')
      ?? url.searchParams.get('reg_id')
      ?? url.searchParams.get('rid')
      ?? url.searchParams.get('session_id')
      ?? url.searchParams.get('id');
}
